import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ProtectedRoute, RoleRoute } from "@/features/auth";
import { UserRole } from "@/entities/user";
import { Loader } from "@/shared/ui";
import { NotFoundPage } from "@/pages/notFound";
import { LoginPage } from "@/pages/login";
import { CoursesPage } from "@/pages/courses";
import { UsersPage } from "@/pages/users";
import { PaymentsPage } from "@/pages/payments";
import { CouponsPage } from "@/pages/coupons";
import { NewCoursePage } from "@/pages/newCourse";
import { SectionsPage } from "@/pages/sections";
import { CoursePage } from "@/pages/course";
import { NewSectionPage } from "@/pages/newSection";
import { SectionPage } from "@/pages/section";
import { LessonsPage } from "@/pages/lessons";
import { NewLessonPage } from "@/pages/newLesson";
import { LessonPage } from "@/pages/lesson";
import { UserPage } from "@/pages/user";
import { NewUserPage } from "@/pages/newUser";
import { CouponPage } from "@/pages/coupon";
import { NewCouponPage } from "@/pages/newCoupon";
import { WordsPage } from "@/pages/words";
import { WordPage } from "@/pages/word";
import { NewWordPage } from "@/pages/newWord";
import { AppLayout } from "./layouts/appLayout";
import { AuthLayout } from "./layouts/authLayout";
import { AppRoutes } from "@/shared/config/routes";
import { RewardsPage } from "@/pages/rewards";
import { RewardPage } from "@/pages/reward";
import { NewRewardPage } from "@/pages/newReward";
import { KoalaPhrasesPage } from "@/pages/koalaPhrases";
import { KoalaPhrasePage } from "@/pages/koalaPhrase";
import { DomainTagPage } from "@/pages/domainTag";
import { NewDomainTagPage } from "@/pages/newDomainTag";
import { NotificationsPage } from "@/pages/notifications";
import { NotificationPage } from "@/pages/notification";
import { NewNotificationPage } from "@/pages/newNotification";

import "./styles/index.css";

const App = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Публичные маршруты */}
          <Route element={<AuthLayout />}>
            <Route path={AppRoutes.LOGIN} element={<LoginPage />} />
          </Route>

          {/* Защищенные маршруты */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* структура курсов */}
            <Route path={AppRoutes.COURSES} element={<CoursesPage />} />
            <Route path={AppRoutes.NEW_COURSE} element={<NewCoursePage />} />
            <Route
              path={`${AppRoutes.COURSE}:courseId`}
              element={<CoursePage />}
            />
            <Route
              path={`${AppRoutes.COURSE}:courseId${AppRoutes.SECTIONS}`}
              element={<SectionsPage />}
            />
            <Route
              path={`${AppRoutes.COURSE}:courseId${AppRoutes.NEW_SECTION}`}
              element={<NewSectionPage />}
            />
            <Route
              path={`${AppRoutes.COURSE}:courseId${AppRoutes.SECTION}:sectionId`}
              element={<SectionPage />}
            />
            <Route
              path={`${AppRoutes.COURSE}:courseId${AppRoutes.SECTION}:sectionId${AppRoutes.LESSONS}`}
              element={<LessonsPage />}
            />
            <Route
              path={`${AppRoutes.COURSE}:courseId${AppRoutes.SECTION}:sectionId${AppRoutes.NEW_LESSON}`}
              element={<NewLessonPage />}
            />
            <Route
              path={`${AppRoutes.COURSE}:courseId${AppRoutes.SECTION}:sectionId${AppRoutes.LESSON}:lessonId`}
              element={<LessonPage />}
            />
            <Route
              path={`${AppRoutes.COURSE}:courseId${AppRoutes.SECTION}:sectionId${AppRoutes.LESSON}:lessonId${AppRoutes.DOMAIN_TAG_NEW}`}
              element={<NewDomainTagPage />}
            />
            <Route
              path={`${AppRoutes.COURSE}:courseId${AppRoutes.SECTION}:sectionId${AppRoutes.LESSON}:lessonId${AppRoutes.DOMAIN_TAG}:domainTagId${AppRoutes.TASKS}`}
              element={<DomainTagPage />}
            />

            {/* пользователи: учитель видит только список своих учеников */}
            <Route path={AppRoutes.USERS} element={<UsersPage />} />

            {/* настройки платформы и управление аккаунтами — только админу */}
            <Route element={<RoleRoute allowedRoles={[UserRole.ADMIN]} />}>
              <Route path={`${AppRoutes.USER}:userId`} element={<UserPage />} />
              <Route path={AppRoutes.NEW_USER} element={<NewUserPage />} />

              {/* слова */}
              <Route path={AppRoutes.WORDS} element={<WordsPage />} />
              <Route path={`${AppRoutes.WORD}:wordId`} element={<WordPage />} />
              <Route path={AppRoutes.NEW_WORD} element={<NewWordPage />} />

              <Route path={AppRoutes.PAYMENTS} element={<PaymentsPage />} />

              {/* cерия входов */}
              <Route path={AppRoutes.REWARDS} element={<RewardsPage />} />
              <Route
                path={`${AppRoutes.REWARD}:rewardId`}
                element={<RewardPage />}
              />
              <Route path={AppRoutes.NEW_REWARD} element={<NewRewardPage />} />

              {/* фразы коалы */}
              <Route
                path={AppRoutes.KOALA_PHRASES}
                element={<KoalaPhrasesPage />}
              />
              <Route
                path={`${AppRoutes.KOALA_PHRASE}:koalaPhraseId`}
                element={<KoalaPhrasePage />}
              />

              {/* купоны */}
              <Route path={AppRoutes.COUPONS} element={<CouponsPage />} />
              <Route
                path={`${AppRoutes.COUPON}:couponId`}
                element={<CouponPage />}
              />
              <Route path={AppRoutes.NEW_COUPON} element={<NewCouponPage />} />

              {/* уведомления */}
              <Route
                path={AppRoutes.NOTIFICATIONS}
                element={<NotificationsPage />}
              />
              <Route
                path={AppRoutes.NEW_NOTIFICATION}
                element={<NewNotificationPage />}
              />
              <Route
                path={`${AppRoutes.NOTIFICATIONS}/:notificationId`}
                element={<NotificationPage />}
              />
            </Route>
          </Route>

          {/* 404 страница */}
          <Route path={AppRoutes.NOT_FOUND} element={<NotFoundPage />} />
          <Route
            path="*"
            element={<Navigate to={AppRoutes.NOT_FOUND} replace />}
          />
        </Routes>
      </Suspense>
      <ToastContainer
        position="bottom-right"
        autoClose={2000}
        hideProgressBar
      />
    </BrowserRouter>
  );
};

export default App;
